import { useEffect } from "react";

/**
 * Publishes the admin nav's measured height as `--bdr-nav-h` on
 * `document.documentElement`, so a viewport-locked page can do
 * `height: calc(100dvh - var(--bdr-nav-h))`.
 *
 * Lives in `utils/` because it has two callers now — the Single Client console
 * and the Tasks console. It was under `SingleClientPage/` when only that page
 * locked itself to the viewport. Any page that calls it gets the variable;
 * nothing declares `--bdr-nav-h` in CSS, so a page that does NOT call it falls
 * back to whatever default the `var()` supplies.
 *
 * WHY MEASURE RATHER THAN HARDCODE. The nav is **not** `position: fixed` — it is
 * `relative` at >=900px and `sticky` below, so it sits in normal flow and
 * consumes real height. That height is not a constant either: it changes at
 * MUI's `xl` breakpoint when the bar switches column→row, and again with
 * role-dependent menu items. Hence a ResizeObserver rather than one `offsetHeight`
 * read on mount.
 *
 * WHY IT RETRIES. The first version measured once, and if the marker was not in
 * the DOM it wrote `0px` and returned — permanently. `AdminNavbar` renders behind
 * a `displayNav &&` gate in `App.js`, so a single frame where that gate has not
 * settled latched zero for the life of the page, and the console then sized
 * itself to a full `100dvh` UNDER a nav that still occupies height — which is
 * exactly the "page scrolls by the height of the navbar" symptom. Now it keeps
 * looking for a short window, and a zero measurement counts as "not ready"
 * rather than as an answer.
 *
 * NOTE ON SPECIFICITY: an inline custom property on `documentElement` outranks
 * any `:root` declaration in a stylesheet. Nothing declares `--bdr-nav-h` in CSS
 * today, but if one ever is, it will not win against this.
 */

// ~1s at 60fps. Long enough for a gated nav to appear, short enough that a route
// genuinely without a nav stops burning frames.
const MAX_FRAMES = 60;

const useNavHeightVar = () => {
  useEffect(() => {
    const root = document.documentElement;
    let observer = null;
    let raf = 0;        // the retry loop's frame
    let measureRaf = 0; // the observer's deferred write — MUST be separate:
                        // `raf` still holds the last (already-fired) retry id
                        // once the marker is found, so sharing it would make
                        // the observer's `if (x) return;` permanently true.
    let frames = 0;

    let lastWritten = -1;

    /*
     * Never touch the DOM for a value that has not changed.
     *
     * This looks like a micro-optimisation and is not. `--bdr-nav-h` feeds
     * `.scc-page { height: calc(100dvh - var(--bdr-nav-h)) }`, so writing it
     * invalidates style for the whole console and re-lays-out everything
     * inside it. Do that from inside a ResizeObserver callback and the
     * resulting resizes are generated DURING the observer's delivery loop —
     * which is precisely the condition Chrome reports as "ResizeObserver loop
     * completed with undelivered notifications", and which CRA's dev overlay
     * escalates from a warning to a full-screen error.
     *
     * It was harmless until something inside `.scc-page` was itself observed.
     * ClientTextMesDisplay's observer is exactly that, so the two started
     * feeding each other. An unchanged value now costs nothing at all.
     */
    const write = (height) => {
      const px = Math.round(height);
      if (px === lastWritten) return;
      lastWritten = px;
      root.style.setProperty("--bdr-nav-h", `${px}px`);
    };

    const attempt = () => {
      const marker = document.querySelector("#admin-nav-height-marker");
      // getBoundingClientRect, not offsetHeight: subpixel, and it reflects any
      // transform the nav happens to carry.
      const height = marker ? marker.getBoundingClientRect().height : 0;

      if (height > 0) {
        write(height);
        /*
         * rAF, so the style write lands on the NEXT frame rather than inside
         * the observer's delivery loop. The guard above already makes the
         * common case a no-op, but a genuine nav resize (MUI's xl breakpoint,
         * a role-dependent menu item) still has to write — and doing that
         * synchronously here is what re-lays-out the console mid-delivery.
         */
        observer = new ResizeObserver(() => {
          if (measureRaf) return;
          measureRaf = requestAnimationFrame(() => {
            measureRaf = 0;
            if (!marker.isConnected) return;
            write(marker.getBoundingClientRect().height);
          });
        });
        observer.observe(marker);
        return;
      }

      // Missing, or present but not laid out yet. Treat both as "not ready".
      if (frames < MAX_FRAMES) {
        frames += 1;
        raf = requestAnimationFrame(attempt);
        return;
      }

      // Genuinely no nav on this route — 0 is the right answer, and writing it
      // explicitly keeps the calc() resolving.
      write(0);
    };

    attempt();

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(measureRaf);
      if (observer) observer.disconnect();
      root.style.removeProperty("--bdr-nav-h");
    };
  }, []);
};

export default useNavHeightVar;
